import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Mail, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../contexts/LangContext";

/**
 * Newsletter signup form — variant: "full" | "inline"
 */
export default function NewsletterSignup({ variant = "full", source = "homepage" }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Inserisci un'email valida");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare il trattamento dei dati");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/newsletter/subscribe", {
        email, name, source, marketing_consent: true, lang: "it",
      });
      setDone(true);
      toast.success(r.data.already_subscribed ? "Sei già iscritto, aggiornati i dati ✓" : "Iscrizione confermata ✓");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'iscrizione");
    } finally { setLoading(false); }
  };

  if (variant === "inline") {
    return (
      <form data-testid="newsletter-inline-form" onSubmit={submit} className="w-full">
        {done ? (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="h-4 w-4" /> Iscrizione confermata — grazie!
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <Input data-testid="newsletter-inline-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter.email_placeholder", "La tua email")} className="bg-white" required />
              <Button data-testid="newsletter-inline-submit" type="submit" disabled={loading} size="sm" className="bg-[hsl(var(--primary))] text-white shrink-0">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("newsletter.button", "Iscriviti")}
              </Button>
            </div>
            <label className="mt-2 flex items-start gap-2 text-[10px] text-muted-foreground cursor-pointer">
              <input data-testid="newsletter-inline-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
              <span>
                {t("newsletter.consent")}
              </span>
            </label>
          </>
        )}
      </form>
    );
  }

  return (
    <section data-testid="newsletter-hero" className="relative bg-gradient-to-br from-[#0a0a0a] via-[#1a0608] to-[#0a0a0a] text-white overflow-hidden border-y border-[#C9A961]/20">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A961 0%, transparent 40%), radial-gradient(circle at 85% 70%, #7A1C2B 0%, transparent 50%)" }} />
      <div className="relative container-xl py-16 md:py-24 grid md:grid-cols-5 gap-10 items-center">
        <div className="md:col-span-3">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-5">
            <Mail className="h-4 w-4" /> Newsletter · una volta alla settimana
          </div>
          <h2 className="editorial-heading text-3xl sm:text-4xl md:text-5xl leading-tight">
            {t("section.newsletter_title", "Resta aggiornato")}
          </h2>
          <p className="mt-4 text-white/75 max-w-lg text-sm md:text-base">
            {t("newsletter.subtitle", "Iscriviti alla nostra newsletter per nuovi prodotti, eventi e offerte esclusive.")}
          </p>
        </div>
        <div className="md:col-span-2">
          {done ? (
            <div data-testid="newsletter-hero-success" className="bg-[#C9A961]/10 border border-[#C9A961]/40 rounded-sm p-6 text-center">
              <Check className="h-10 w-10 text-[#C9A961] mx-auto mb-3" />
              <div className="editorial-heading text-2xl">Benvenuto.</div>
              <p className="text-sm text-white/70 mt-2">La prima email arriva lunedì alle 08:00.</p>
            </div>
          ) : (
            <form data-testid="newsletter-hero-form" onSubmit={submit} className="space-y-3 bg-white/[0.02] border border-white/10 rounded-sm p-5 backdrop-blur">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1.5">Nome</label>
                <Input data-testid="newsletter-hero-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Come ti chiami?" className="bg-black/40 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1.5">Email *</label>
                <Input data-testid="newsletter-hero-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter.email_placeholder", "La tua email")} required className="bg-black/40 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer mt-2">
                <input data-testid="newsletter-hero-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" required />
                <span>{t("newsletter.consent")}</span>
              </label>
              <Button data-testid="newsletter-hero-submit" type="submit" disabled={loading} className="w-full bg-[#C9A961] hover:bg-white text-black font-semibold uppercase tracking-[0.2em] text-xs h-12 mt-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                {t("newsletter.cta")}
              </Button>
              <p className="text-[10px] text-white/40 text-center pt-1">Conforme GDPR · double opt-in · cancellazione con 1 clic</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
